import React, { useContext, useEffect, useState } from 'react';
import { UserContext } from 'contexts/user';
import { ModalContext } from 'contexts/modal';
import Button from 'components/control/button';
import Link from 'components/link';
import MapIcon from 'components/icon/map-icon';
import NewsIcon from 'components/icon/news-icon';
import TagIcon from 'components/icon/tag-icon';
import HollowHeartIcon from 'components/icon/hollow-heart-icon';
import { useRouter } from 'next/router';
import { buildClassName } from 'utils/build-class-name';
import getSearchPageParameters from 'utils/get-search-page-redirect-url';
import { formatQueryIntoURLQueryParams } from 'utils/listing-query-helper';
import { IPreferences, PreferencesContext } from 'contexts/preferences';
import { DEFAULT_LISTING_PARAMS_WHOLE_US, DEFAULT_LISTING_PARAMS_WHOLE_CANADA } from 'contexts/preferences/listing-params/defaults';
import styles from './style.module.scss';
import { trackEvent } from 'utils/google-tag-manager';
import { GTM_CLICK_MOBILE_BOT_NAV_ON_MAP_MY_FAV } from 'constants/events';
import { ThemeNames } from 'types/themes';
import { useThemeContext } from 'contexts';
import useDebounce from 'hooks/use-debounce';
import { useRecentlySoldNav } from 'hooks/use-recently-sold-nav';
import { AREA_LISTINGS_ROUTE, getRouteNameFromPath } from 'components/dynamic-page/route-matchers';
import { fetchAreaPolygons } from 'data/search/map/fetcher';
import { parseWktPoint } from 'components/search-es/geo_helper';
import { generatePolygonSlugsFromPathname } from 'data/search/map/utils';

import type { IUserContext } from 'contexts/user';
import type { IModalContext } from 'contexts/modal';

const MobileBottomNav = ({ handleMapListToggleButtonClick }: {handleMapListToggleButtonClick?: () => void}) => {
  const { isAuthenticated } = useContext(UserContext) as IUserContext;
  const { openModal } = useContext(ModalContext) as IModalContext;
  const { listingParams } = useContext(PreferencesContext) as IPreferences;
  const { themeName } = useThemeContext();
  const isZoocasaTenant = themeName === ThemeNames.ZOOCASA;
  const router = useRouter();
  const { asPath } = router;
  const { mobileSoldUrl, mobileSoldText, onSoldLinkClick } = useRecentlySoldNav();

  const isOnSoldRoute = asPath.includes('not-available-sold');
  const isOnSearchRoute = asPath.startsWith('/search') && !isOnSoldRoute;
  const onFavouritesRoute = asPath.startsWith('/account/saved/favourites');
  const isOnAreaPage = getRouteNameFromPath(router.asPath) === AREA_LISTINGS_ROUTE;

  const clickFavourites = () => {
    trackEvent(GTM_CLICK_MOBILE_BOT_NAV_ON_MAP_MY_FAV);
    if (isAuthenticated) {
      router.push('/account/saved/favourites');
    } else {
      openModal('login-registration');
    }
  };
  const [isVisible, setIsVisible] = useState(true);
  const [lastScrollY, setLastScrollY] = useState(0);

  const handleScroll = () => {
    if (window.scrollY > lastScrollY) {
      setIsVisible(false);
    } else {
      setIsVisible(true);
    }

    setLastScrollY(window.scrollY);
  };
  const debouncedScroll = useDebounce(handleScroll, 50);

  useEffect(() => {
    if (isOnAreaPage || isOnSearchRoute) {
      window.addEventListener('scroll', debouncedScroll, { passive: true });
      return () => window.removeEventListener('scroll', debouncedScroll);
    }
  }, [isOnAreaPage, isOnSearchRoute, debouncedScroll]);

  const navigateToSearch = async () => {
    if (isOnAreaPage) {
      if (listingParams.filter && listingParams.filter.latitude && listingParams.filter.longitude) {
        router.push(`/search?${formatQueryIntoURLQueryParams(listingParams.filter)}&sort=${listingParams.sort}`);
        return;
      }

      // If this country wide area page use our default listing params for that country
      const pathFirstPart = router.asPath.split('/')[1];
      if (['ca-real-estate', 'us-real-estate'].includes(pathFirstPart)) {
        const defaultListingParams = pathFirstPart === 'ca-real-estate' ? DEFAULT_LISTING_PARAMS_WHOLE_CANADA : DEFAULT_LISTING_PARAMS_WHOLE_US;
        listingParams.setLatitude(defaultListingParams.filter.latitude);
        listingParams.setLongitude(defaultListingParams.filter.longitude);
        listingParams.setZoom(defaultListingParams.filter.zoom);
        listingParams.setAreaName(defaultListingParams.filter.areaName);

        return router.push(`/search?${formatQueryIntoURLQueryParams(listingParams.filter)}&sort=${listingParams.sort}`);
      }

      const { province, subDivision, neighbourhood } = generatePolygonSlugsFromPathname(router.asPath);
      let slugToUse: string | null = null;
      let areaData = null;

      if (neighbourhood) {
        try {
          const response = await fetchAreaPolygons({ slug: neighbourhood });
          if (response.data && response.data.length > 0) {
            areaData = response.data[0];
            slugToUse = neighbourhood;
          }
        } catch (error) {
          console.error('Failed to fetch neighbourhood data:', error);
        }
      }

      if (!areaData && subDivision) {
        try {
          const response = await fetchAreaPolygons({ slug: subDivision });
          if (response.data && response.data.length > 0) {
            areaData = response.data[0];
            slugToUse = subDivision;
          }
        } catch (error) {
          console.error('Failed to fetch subDivision data:', error);
        }
      }

      if (!areaData && province) {
        try {
          const response = await fetchAreaPolygons({ slug: province });
          if (response.data && response.data.length > 0) {
            areaData = response.data[0];
            slugToUse = province;
          }
        } catch (error) {
          console.error('Failed to fetch province data:', error);
        }
      }

      if (areaData && slugToUse) {
        const { centroid, fullName } = areaData;
        const { latitude, longitude } = parseWktPoint(centroid);
        listingParams.setLatitude(latitude);
        listingParams.setLongitude(longitude);
        listingParams.setSlug(slugToUse);
        listingParams.setAreaName(fullName);
        // If it's not 'ca' or 'us' explicitly, and no slugs were generated, it might be an unhandled case or a new country.
        // For now, it will proceed with potentially empty/default listingParams if not 'ca' or 'us'.
      }
    }
    router.push(`/search?${formatQueryIntoURLQueryParams(listingParams.filter)}&sort=${listingParams.sort}`);
  };

  return (
    <div className={buildClassName(styles.component, isVisible ? styles['is-visible'] : styles['is-hidden'])}>
      {!(isOnAreaPage || isOnSearchRoute) ? null : <Button
        label='Map'
        className={buildClassName(styles['map-chip'], !isZoocasaTenant && styles['exp-theme'], isOnSearchRoute && styles.active)}
        Icon={MapIcon}
        onClick={isOnSearchRoute ? handleMapListToggleButtonClick : navigateToSearch}
      />}
      <Link href={mobileSoldUrl} className={buildClassName(styles.item, isOnSoldRoute && styles.active)} onClick={onSoldLinkClick}>
        <>
          <TagIcon className={styles.icon} />
          {mobileSoldText}
        </>
      </Link>
      <div onClick={clickFavourites} className={buildClassName(styles.item, onFavouritesRoute && styles.active)}>
        <HollowHeartIcon className={buildClassName(styles.icon, styles['icon-heart'])} />
          Favourites
      </div>
      <Link href={isOnAreaPage ? '/search' : getSearchPageParameters(listingParams)} className={buildClassName(styles.item, isOnSearchRoute && styles.active)}>
        <>
          <MapIcon className={styles.icon} />
          Map
        </>
      </Link>
      {isZoocasaTenant &&
        <a href="https://www.zoocasa.com/blog/" className={styles.item}>
          <NewsIcon className={styles.icon} />
          Articles
        </a>
      }
    </div>
  );
};

export default MobileBottomNav;
